<template>
  <div>
    <div class="mb-4">
      <div class="font-weight-medium mb-1">1 Shut {{ preflight.sourceHostname }}</div>
      <step-button
        name="canShutdown"
        :label="shutText"
        :steps="preflight.steps"
        :busy="busy"
        :loading="loadingKey === 'shut'"
        @click="shutOpen = true"
      />
      <div v-if="status.shut" :class="toneClass(status.shut.tone)">Status: {{ status.shut.text }}</div>
      <div v-if="status.shut && status.shut.hint" class="caption grey--text text--darken-2">{{ status.shut.hint }}</div>

      <div class="mt-3">
        <div class="font-weight-medium mb-1">Commit statics</div>
        <div>{{ commit.text }}</div>
        <div v-if="commitNext" class="font-weight-medium">{{ commitNext }}</div>
        <step-button
          name="canCommit"
          :label="`Commit ${commit.pending} statics...`"
          :steps="preflight.steps"
          :busy="busy"
          :loading="loadingKey === 'commit'"
          @click="commitOpen = true"
        />
        <step-button
          v-if="commit.failed > 0"
          name="canCommit"
          :label="`Retry failed (${commit.failed})`"
          :steps="preflight.steps"
          color="secondary"
          :busy="busy"
          :loading="loadingKey === 'commit-retry'"
          @click="askRetryCommit"
        />
        <v-alert v-if="commitResult" :type="commitResult.type" dense outlined class="mt-2 mb-0">{{ commitResult.text }}</v-alert>
        <v-alert v-if="commitOnline" type="warning" dense outlined class="mt-2 mb-0">{{ commitOnline }}</v-alert>
        <v-alert v-if="commitBatch" :type="commitBatch.type" dense outlined class="mt-2 mb-0">
          {{ commitBatch.text }}
          <v-expansion-panels v-if="commitBatch.rows.length" accordion flat class="mt-1">
            <v-expansion-panel>
              <v-expansion-panel-header class="px-0">Rows ({{ commitBatch.rows.length }})</v-expansion-panel-header>
              <v-expansion-panel-content class="px-0">
                <ul class="mb-0">
                  <li v-for="(r, i) in commitBatch.rows" :key="`commit-row-${i}`">
                    <strong>{{ r.avcId }}</strong> {{ r.outcome }}: {{ r.message }}
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-alert>
      </div>

      <v-alert v-if="notice && notice.warning" type="warning" dense outlined class="mt-2 mb-0">{{ notice.warning }}</v-alert>
      <v-alert v-if="notice" type="success" dense outlined class="mt-2 mb-0">
        <div>
          {{ notice.title }}
          <v-btn small text class="ml-2" @click="copyNotice">Copy message</v-btn>
          <span v-if="copyNote" class="caption">{{ copyNote }}</span>
        </div>
        <div class="caption">{{ notice.message }}</div>
      </v-alert>
    </div>

    <div class="mb-4">
      <div class="font-weight-medium mb-1">2 Optus re-point</div>
      <div v-if="waiting.text" :class="toneClass(waiting.tone)">{{ waiting.text }}</div>
      <div v-if="waiting.advice" class="orange--text text--darken-3">{{ waiting.advice }}</div>
      <template v-if="evidenceOn">
        <div v-if="evidence.line">
          Evidence: {{ evidence.line }}
          <v-btn x-small text color="primary" :disabled="busy || checkState.disabled" :title="checkState.reason" @click="refreshReconnect">Refresh</v-btn>
          <span v-if="checkState.disabled" class="caption grey--text text--darken-1">{{ checkState.reason }}</span>
        </div>
        <div v-else-if="reconnectError" class="orange--text text--darken-3">Evidence not available: {{ reconnectError }}</div>
        <div v-else class="grey--text text--darken-1">Evidence not read yet (the page checks every minute).</div>
        <div v-if="evidence.message" :class="toneClass(evidence.tone)">{{ evidence.message }}</div>
      </template>
      <div v-else-if="idleText" class="grey--text text--darken-1">{{ idleText }}</div>
      <div v-if="record" class="grey--text text--darken-2">{{ pointer }}</div>
      <div v-if="status.rollback" :class="toneClass(status.rollback.tone)">{{ status.rollback.text }}</div>
      <v-alert v-if="rolledBackNotice" type="success" dense outlined class="mt-2 mb-0">
        <div>
          {{ rolledBackNotice.title }}
          <v-btn small text class="ml-2" @click="copyText(rolledBackNotice.message)">Copy message</v-btn>
          <span v-if="copyNote" class="caption">{{ copyNote }}</span>
        </div>
        <div class="caption">{{ rolledBackNotice.message }}</div>
      </v-alert>
    </div>

    <div class="mb-4">
      <div class="font-weight-medium mb-1">3 Confirm</div>
      <step-button
        :override="confirmState"
        label="Confirm re-point..."
        :busy="busy"
        :loading="loadingKey === 'confirm'"
        @click="confirmOpen = true"
      />
      <div v-if="record && record.repointConfirmedAt" class="green--text text--darken-2">
        Re-point confirmed {{ when(record.repointConfirmedAt) }} by {{ record.repointConfirmedBy }}{{ record.repointWithoutShut ? " (source shut failed, override)" : "" }}
      </div>
    </div>

    <div class="mb-2">
      <div class="font-weight-medium mb-1">4 Activate</div>
      <step-button
        name="canActivate"
        :label="`Activate on ${preflight.targetHostname}...`"
        :steps="preflight.steps"
        :busy="busy"
        :loading="loadingKey === 'activate'"
        @click="activateOpen = true"
      />
      <div v-if="record && record.activatedAt" class="green--text text--darken-2">
        Activated {{ when(record.activatedAt) }} by {{ record.activatedBy }}
      </div>
      <div v-if="nextLine" class="font-weight-medium">{{ nextLine }}</div>
      <v-alert v-if="orderBanner" :type="orderBanner.type" dense outlined class="mt-2 mb-0">{{ orderBanner.text }}</v-alert>
      <v-alert v-if="refusal" :type="refusal.type" dense outlined class="mt-2 mb-0">
        {{ refusal.text }}
        <v-simple-table v-if="refusal.conflicts.length" dense class="mt-2">
          <thead>
            <tr><th>AVC</th><th>Inner VLAN</th><th>Held by</th></tr>
          </thead>
          <tbody>
            <tr v-for="c in refusal.conflicts" :key="`conflict-${c.avcId}`">
              <td>{{ c.avcId }}</td><td>{{ c.innerVlan }}</td><td>{{ c.heldByAvcId }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-alert>
      <div v-if="activation" class="mt-1">{{ activationText }}</div>
      <div v-if="verifying && verifyProgress" class="mt-2">
        <v-progress-linear :value="((verifyProgress.index - 1) / verifyProgress.total) * 100" height="18" />
        <div class="mt-1">{{ verifyProgressText }}</div>
      </div>
    </div>

    <v-alert v-if="!followUpList.empty" type="warning" dense outlined class="mt-3 mb-0">
      <div class="font-weight-medium">Follow-ups</div>
      <div v-for="item in followUpList.items" :key="`follow-${item.key}`" class="d-flex align-center flex-wrap">
        <span :class="`${toneClass(item.tone)} mr-3`">{{ item.text }}</span>
        <step-button
          :override="flagState(item.flag)"
          :label="item.actionLabel"
          outlined
          :busy="busy"
          :loading="loadingKey === `follow-${item.key}`"
          @click="followUp(item)"
        />
      </div>
    </v-alert>

    <v-alert v-if="alert" :type="alert.type" dense outlined class="mt-3 mb-0">
      {{ alert.text }}
      <v-btn v-if="alert.type === 'error'" small text class="ml-2" @click="retryLast">Retry</v-btn>
    </v-alert>

    <confirm-action
      :dialog="confirm.open"
      :text="confirm.text"
      :proceed-text="confirm.proceedText"
      cancel-text="Cancel"
      @onBtnClick="onConfirm"
    />
    <type-to-confirm v-model="shutOpen" v-bind="shutDlg" :cvc-name="preflight.cvcName" @proceed="shut" />
    <type-to-confirm v-model="confirmOpen" v-bind="confirmDlg" :cvc-name="preflight.cvcName" @proceed="confirmRepoint" />
    <type-to-confirm v-model="activateOpen" v-bind="activateDlg" :cvc-name="preflight.cvcName" @proceed="activate" />
    <type-to-confirm v-model="commitOpen" v-bind="commitDlg" :cvc-name="preflight.cvcName" @proceed="commitNow('commit')" />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { formatDual } from "@/utils/zonedTime";
import {
  activateDialog,
  activationNextLine,
  activationRefusal,
  activationSummary,
  batchSummary,
  commitDialog,
  commitNextLine,
  commitOnlineLine,
  commitResultLine,
  commitSummary,
  confirmButton,
  confirmDialog,
  confirmRepointRequest,
  cutoverStatus,
  dialogProps,
  flagButton,
  followUps,
  outOfOrderBanner,
  repointEvidence,
  shutDialog,
  shutLabel,
  showEvidence,
  stepButton,
  optusNotice,
  waitingInfo,
} from "@/utils/cvcMigrationPage";
import { ROLLBACK_POINTER, rollbackNotice } from "@/utils/cvcMigrationRollback";
import StepButton from "./step-button";
import TypeToConfirm from "./type-to-confirm";
import SectionActions from "./section-actions";
import VerifyRun from "./verify-run";

const TONE_CLASS = {
  green: "green--text text--darken-2",
  amber: "orange--text text--darken-3",
  red: "red--text text--darken-2",
  grey: "grey--text text--darken-2",
};

// Section C: the four steps of the cutover. Each button is on only when the
// server says so; the dialogs carry the exact consequences.
export default {
  name: "CvcMigrationCutover",
  components: { StepButton, TypeToConfirm },
  mixins: [SectionActions, VerifyRun],
  props: {
    preflight: { type: Object, required: true },
    cvcId: { type: Number, required: true },
    targetHostId: { type: Number, required: true },
    now: { type: Date, required: true },
    lastVerify: { type: Object, default: null },
    // The last reconnection check the page read, and why the last read failed.
    reconnect: { type: Object, default: null },
    reconnectError: { type: String, default: "" },
  },
  data: () => ({
    pointer: ROLLBACK_POINTER,
    shutOpen: false,
    confirmOpen: false,
    activateOpen: false,
    commitOpen: false,
    commitBatch: null,
    commitRaw: null,
    refusal: null,
    activation: null,
    copyNote: "",
  }),
  computed: {
    record () {
      return this.preflight.record;
    },
    rows () {
      return this.preflight.rows || [];
    },
    status () {
      return cutoverStatus(this.record, this.now, this.preflight.poiState);
    },
    notice () {
      return optusNotice(this.preflight);
    },
    rolledBackNotice () {
      return rollbackNotice(this.preflight);
    },
    waiting () {
      return this.preflight.steps && this.preflight.steps.phase === "waitingRepoint"
        ? waitingInfo(this.preflight, this.now)
        : { text: "", tone: "grey", advice: "" };
    },
    evidenceOn () {
      return showEvidence(this.preflight, this.now, this.reconnect);
    },
    evidence () {
      return repointEvidence(this.preflight, this.reconnect);
    },
    idleText () {
      const phase = this.preflight.steps && this.preflight.steps.phase;
      if (phase === "cancelled") return "Migration cancelled.";
      return ["none", "planning", "prebuilding", "ready"].includes(phase) ? `Nothing to wait for until ${this.preflight.sourceHostname} is shut.` : "";
    },
    checkState () {
      return stepButton(this.preflight.steps, "canVerifyReconnected");
    },
    shutText () {
      return shutLabel(this.preflight);
    },
    confirmState () {
      const button = confirmButton(this.preflight.steps);
      return { disabled: button.disabled, reason: button.reason };
    },
    shutDlg () {
      return dialogProps(shutDialog(this.preflight, this.now));
    },
    confirmDlg () {
      return dialogProps(confirmDialog(this.preflight, this.reconnect));
    },
    activateDlg () {
      return dialogProps(activateDialog(this.preflight));
    },
    commitResult () {
      return commitResultLine(this.preflight);
    },
    commit () {
      return commitSummary(this.rows, this.commitRaw, this.commitResult ? this.commitResult.failed : 0);
    },
    commitNext () {
      return commitNextLine(this.preflight, this.reconnect);
    },
    commitOnline () {
      return commitOnlineLine(this.reconnect);
    },
    commitDlg () {
      return dialogProps(commitDialog(this.rows, this.preflight, this.reconnect));
    },
    activationText () {
      return activationSummary(this.activation);
    },
    nextLine () {
      return activationNextLine(this.preflight);
    },
    orderBanner () {
      return outOfOrderBanner(this.preflight);
    },
    // The lists start from the activation answer and shrink as the page reloads.
    followUpList () {
      return followUps(this.activation, this.lastVerify, {
        activated: !!(this.record && this.record.activatedAt),
        rows: this.preflight.rows,
        record: this.record,
      });
    },
  },
  methods: {
    ...mapActions("cvcMigration", [
      "cvcMigrationShutdownSource",
      "cvcMigrationConfirmRepoint",
      "cvcMigrationActivate",
      "cvcMigrationCommitStatics",
      "cvcMigrationBuild",
      "cvcMigrationRepolicy",
      "cvcMigrationVerifyReconnected",
    ]),
    toneClass (tone) {
      return TONE_CLASS[tone] || TONE_CLASS.grey;
    },
    when (utc) {
      return formatDual(utc, this.preflight.poiState);
    },
    flagState (flag) {
      return flagButton(this.preflight.steps, flag);
    },
    copyNotice () {
      return this.copyText(this.notice.message);
    },
    async copyText (text) {
      try {
        await navigator.clipboard.writeText(text);
        this.copyNote = "Copied";
      } catch (e) {
        this.copyNote = "Could not copy - select the text below";
      }
    },
    shut () {
      this.run("shut", () => this.cvcMigrationShutdownSource({ cvcId: this.cvcId }));
    },
    commitNow (key) {
      this.run(key, async () => {
        const batch = await this.cvcMigrationCommitStatics({ cvcId: this.cvcId });
        this.commitRaw = batch;
        this.commitBatch = batchSummary("Commit", batch, new Date(), this.preflight.poiState);
      });
    },
    askRetryCommit () {
      this.ask(
        `Retry the ${this.commit.failed} failed statics? Rows that are done are skipped.`,
        "Retry",
        () => this.commitNow("commit-retry")
      );
    },
    confirmRepoint (answer) {
      this.run("confirm", () => this.cvcMigrationConfirmRepoint({ cvcId: this.cvcId, ...confirmRepointRequest(this.preflight, answer) }));
    },
    // Usher refuses an activation with a list (AVCs with a queued change, or
    // VLAN conflicts): that is shown in place of the generic failure.
    activate () {
      this.refusal = null;
      this.activation = null;
      return this.run("activate", async () => {
        try {
          this.activation = await this.cvcMigrationActivate({ cvcId: this.cvcId });
        } catch (e) {
          const refusal = activationRefusal(e);
          if (!refusal) throw e;
          this.refusal = refusal;
          return;
        }
        await this.verifyRun();
      });
    },
    followUp (item) {
      if (item.key === "notShut") {
        this.shutOpen = true;
        return;
      }
      this.run(`follow-${item.key}`, () => (item.key === "repolicy"
        ? this.cvcMigrationRepolicy({ cvcId: this.cvcId, avcIds: item.ids })
        : this.cvcMigrationBuild({ cvcId: this.cvcId, targetHostId: this.targetHostId, avcIds: item.ids })));
    },
    refreshReconnect () {
      this.run("reconnect", async () => {
        this.$emit("reconnect", await this.cvcMigrationVerifyReconnected({ cvcId: this.cvcId, refresh: false }));
      });
    },
  },
};
</script>
